import { useEffect, useMemo, useState } from "react";
import { api, formatPrice } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Armchair, Suitcase, Info, CheckCircle, ArrowsClockwise, XCircle } from "@phosphor-icons/react";

/* ──────────────── Fare Rules ──────────────── */
function Rule({ icon: I, label, cond }) {
  let text, tone;
  if (!cond) { text = "Information unavailable — airline rules apply"; tone = "text-[#666460]"; }
  else if (cond.allowed === false) { text = "Not allowed"; tone = "text-red-700"; }
  else if (cond.allowed && cond.penalty_amount && cond.penalty_currency) {
    text = `Allowed · fee ${formatPrice(parseFloat(cond.penalty_amount), cond.penalty_currency)}`;
    tone = "text-[#1C1C1C]";
  } else if (cond.allowed) { text = "Allowed · airline fee may apply"; tone = "text-[#1C1C1C]"; }
  else { text = "Information unavailable"; tone = "text-[#666460]"; }
  return (
    <div className="flex items-start gap-3 py-2">
      <I size={18} className="text-[#C29B62] mt-0.5" />
      <div>
        <p className="text-xs tracking-[0.18em] uppercase font-semibold text-[#666460]">{label}</p>
        <p className={`text-sm ${tone}`}>{text}</p>
      </div>
    </div>
  );
}

function FareRules({ data }) {
  const c = data?.conditions || {};
  const slices = data?.slice_conditions || [];
  const incl = data?.baggage_allowance || {};
  // Aggregate baggage across segments for a clean summary
  const summary = {};
  Object.values(incl).forEach((segMap) => {
    Object.values(segMap).forEach((bags) => {
      Object.entries(bags).forEach(([type, b]) => {
        summary[type] = summary[type] || { quantity: 0, weight_kg: null };
        summary[type].quantity = Math.max(summary[type].quantity, b.quantity || 0);
        if (b.weight && b.weight.value) summary[type].weight_kg = `${b.weight.value} ${b.weight.unit || "kg"}`;
      });
    });
  });

  return (
    <Card className="rounded-none border-[#E0DDD6] p-6" data-testid="fare-rules">
      <p className="tcp-overline text-[#C29B62] mb-4">Fare Rules</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
        <Rule icon={ArrowsClockwise} label="Changes before departure" cond={c.change_before_departure} />
        <Rule icon={XCircle} label="Refunds before departure" cond={c.refund_before_departure} />
      </div>
      {slices.length > 1 && (
        <div className="mt-4 pt-4 border-t border-[#E0DDD6]">
          <p className="text-xs tracking-[0.18em] uppercase font-semibold text-[#666460] mb-2">Per-leg policy</p>
          {slices.map((s) => (
            <div key={s.id} className="text-sm text-[#1C1C1C] py-1">
              <span className="font-semibold">{s.origin} → {s.destination}</span>
              <span className="text-[#666460] ml-3">
                {s.conditions?.change_before_departure?.allowed ? "Changes allowed" : "Changes restricted"} ·{" "}
                {s.conditions?.refund_before_departure?.allowed ? "Refundable" : "Non-refundable"}
              </span>
            </div>
          ))}
        </div>
      )}
      <div className="mt-4 pt-4 border-t border-[#E0DDD6]">
        <p className="text-xs tracking-[0.18em] uppercase font-semibold text-[#666460] mb-2">Included baggage</p>
        {Object.keys(summary).length === 0 ? (
          <p className="text-sm text-[#666460] flex items-center gap-2"><Info size={14} /> Baggage allowance information not provided by the carrier.</p>
        ) : (
          <ul className="text-sm text-[#1C1C1C] space-y-1">
            {Object.entries(summary).map(([type, info]) => (
              <li key={type} className="flex items-center gap-2">
                <CheckCircle size={14} className="text-[#C29B62]" />
                <span className="capitalize">{type.replace("_", " ")}</span>
                <span className="text-[#666460]">· {info.quantity} pc{info.weight_kg ? ` · up to ${info.weight_kg}` : ""}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
}

/* ──────────────── Seat Map ──────────────── */
function SeatElement({ el, segId, segLabel, activePaxId, activePaxLabel, selection, onPick }) {
  if (el.type !== "seat") {
    if (el.type === "empty" || el.type === "aisle") return <div className="w-9 h-9" />;
    if (el.type === "exit_row") return <div className="col-span-full text-center text-[10px] tracking-[0.2em] text-red-600 py-1">— EMERGENCY EXIT —</div>;
    return <div className="w-9 h-9" />;
  }
  const services = el.available_services || [];
  const service = services.find((s) => (s.passenger_ids || []).includes(activePaxId)) || services[0];
  const available = !!service;
  const selected = available && selection[segId]?.[activePaxId]?.serviceId === service.id;
  const takenByOther = available && Object.entries(selection[segId] || {})
    .some(([pid, s]) => pid !== activePaxId && s?.serviceId === service.id);

  return (
    <button
      type="button"
      disabled={!available || takenByOther}
      onClick={() => available && onPick(segId, segLabel, {
        serviceId: service.id,
        designator: el.designator,
        total_amount: service.total_amount,
        total_currency: service.total_currency,
        passenger_label: activePaxLabel,
      })}
      data-testid={`seat-${segId}-${el.designator}`}
      title={service ? `${el.designator} · ${formatPrice(parseFloat(service.total_amount || 0), service.total_currency || "USD")}` : el.designator}
      className={`w-9 h-9 text-[10px] font-semibold rounded-sm border transition-colors flex items-center justify-center ${
        !available
          ? "bg-[#E0DDD6] border-[#E0DDD6] text-[#A09E9A] cursor-not-allowed"
          : selected
            ? "bg-[#C29B62] border-[#C29B62] text-[#0A0A0B]"
            : takenByOther
              ? "bg-[#1C1C1C]/10 border-[#1C1C1C]/10 text-[#666460] cursor-not-allowed"
              : "bg-white border-[#C29B62]/40 text-[#1C1C1C] hover:bg-[#C29B62]/10"
      }`}
    >
      {el.designator?.slice(-2) || ""}
    </button>
  );
}

function SeatMaps({ maps, passengers, segments, selection, onChange }) {
  const [activePax, setActivePax] = useState(passengers[0]?.id);
  useEffect(() => { if (!passengers.find((p) => p.id === activePax)) setActivePax(passengers[0]?.id); }, [passengers, activePax]);

  if (!maps?.length) {
    return (
      <Card className="rounded-none border-[#E0DDD6] p-6" data-testid="seat-map-unavailable">
        <p className="tcp-overline text-[#C29B62] mb-3">Seat Selection</p>
        <p className="text-sm text-[#666460] flex items-center gap-2"><Info size={14} /> Seat selection is not offered for this fare. Your seat will be assigned at check-in.</p>
      </Card>
    );
  }

  const segLabel = (segId) => {
    const s = segments.find((x) => x.id === segId);
    return s ? `${s.origin} → ${s.destination}` : "";
  };
  const activePaxLabel = passengers.find((p) => p.id === activePax)?.label || "";

  const onPick = (segId, segLbl, sel) => {
    onChange({ ...selection, [segId]: { ...(selection[segId] || {}), [activePax]: { ...sel, segment_label: segLbl } } });
  };

  return (
    <Card className="rounded-none border-[#E0DDD6] p-6" data-testid="seat-map">
      <p className="tcp-overline text-[#C29B62] mb-4">Seat Selection</p>
      <div className="flex flex-wrap items-center gap-2 mb-5">
        {passengers.map((p, i) => (
          <button key={p.id} type="button" onClick={() => setActivePax(p.id)}
            data-testid={`seat-pax-${i}`}
            className={`px-3 py-1.5 text-xs tracking-[0.16em] uppercase border ${
              activePax === p.id ? "bg-[#0A0A0B] text-[#F7F5F0] border-[#0A0A0B]" : "border-[#E0DDD6] text-[#666460]"
            }`}>
            {p.label || `Passenger ${i + 1}`}
          </button>
        ))}
      </div>

      <div className="space-y-8">
        {maps.map((sm) => (
          <div key={sm.id}>
            <p className="text-xs tracking-[0.18em] uppercase font-semibold text-[#666460] mb-3">
              {segLabel(sm.segment_id) || `Segment ${sm.segment_id?.slice(-6)}`} · {sm.cabins?.[0]?.cabin_class || "Cabin"}
            </p>
            <div className="space-y-1 max-h-[420px] overflow-y-auto pr-2 bg-[#F7F5F0] p-3">
              {(sm.cabins || []).flatMap((c) => c.rows || []).map((row, ri) => (
                <div key={`${sm.id}-${ri}`} className="flex items-center gap-1 justify-center">
                  {(row.sections || []).map((sec, si) => (
                    <div key={`${sm.id}-${ri}-sec${si}`} className="flex items-center gap-1">
                      {(sec.elements || []).map((el, ei) => (
                        <SeatElement
                          key={el.designator || `${sm.id}-${ri}-${si}-el${ei}`}
                          el={el}
                          segId={sm.segment_id}
                          segLabel={segLabel(sm.segment_id)}
                          activePaxId={activePax}
                          activePaxLabel={activePaxLabel}
                          selection={selection}
                          onPick={onPick}
                        />
                      ))}
                      {si < (row.sections.length - 1) && <div className="w-4" />}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <p className="text-xs text-[#666460] mt-4 flex items-center gap-4">
        <span className="flex items-center gap-1"><span className="w-3 h-3 bg-white border border-[#C29B62]/40" /> Available</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 bg-[#C29B62]" /> Selected</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 bg-[#E0DDD6]" /> Unavailable</span>
      </p>
    </Card>
  );
}

/* ──────────────── Baggage Selector ──────────────── */
function BaggageSelector({ services, passengers, segments, baggage, onChange }) {
  if (!services?.length) {
    return (
      <Card className="rounded-none border-[#E0DDD6] p-6" data-testid="bags-unavailable">
        <p className="tcp-overline text-[#C29B62] mb-3">Extra Baggage</p>
        <p className="text-sm text-[#666460] flex items-center gap-2"><Info size={14} /> No additional baggage is available for purchase on this fare.</p>
      </Card>
    );
  }

  const paxLabel = (id) => {
    const idx = passengers.findIndex((p) => p.id === id);
    return idx >= 0 ? (passengers[idx].label || `Passenger ${idx + 1}`) : "All passengers";
  };
  const segLabel = (id) => {
    const s = segments.find((x) => x.id === id);
    return s ? `${s.origin} → ${s.destination}` : "All segments";
  };

  const update = (sid, q) => onChange({ ...baggage, [sid]: Math.max(0, q) });

  return (
    <Card className="rounded-none border-[#E0DDD6] p-6" data-testid="bags-selector">
      <p className="tcp-overline text-[#C29B62] mb-4">Extra Baggage</p>
      <div className="divide-y divide-[#E0DDD6]">
        {services.map((s) => {
          const qty = baggage[s.id] || 0;
          const max = s.maximum_quantity || 1;
          const meta = s.metadata || {};
          const weight = meta.maximum_weight_kg ? `${meta.maximum_weight_kg} kg` : null;
          return (
            <div key={s.id} className="flex items-center justify-between py-4" data-testid={`bag-${s.id}`}>
              <div className="flex items-start gap-3">
                <Suitcase size={22} className="text-[#C29B62] mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-[#1C1C1C] capitalize">
                    Extra {(meta.type || "checked").replace("_", " ")} bag
                    {weight ? <span className="text-[#666460] font-normal"> · up to {weight}</span> : null}
                  </p>
                  <p className="text-xs text-[#666460]">
                    {(s.passenger_ids || []).map(paxLabel).join(", ") || "All passengers"}
                    {" · "}
                    {(s.segment_ids || []).map(segLabel).join(", ") || "All segments"}
                  </p>
                  <p className="text-xs text-[#666460]">Max {max} · {formatPrice(parseFloat(s.total_amount || 0), s.total_currency || "USD")} each</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => update(s.id, qty - 1)} disabled={qty === 0}
                  data-testid={`bag-${s.id}-minus`}
                  className="w-8 h-8 border border-[#E0DDD6] text-[#1C1C1C] disabled:opacity-30">−</button>
                <span className="w-7 text-center text-sm font-semibold" data-testid={`bag-${s.id}-qty`}>{qty}</span>
                <button type="button" onClick={() => update(s.id, qty + 1)} disabled={qty >= max}
                  data-testid={`bag-${s.id}-plus`}
                  className="w-8 h-8 border border-[#C29B62] text-[#C29B62] disabled:opacity-30">+</button>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

/* ──────────────── Main step ──────────────── */
export default function SeatsAndExtras({ offerId, paxList, onUpdateExtras }) {
  const [ancillaries, setAncillaries] = useState(null);
  const [seatMaps, setSeatMaps] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seatSel, setSeatSel] = useState({});         // {segId: {duffelPaxId: service}}
  const [bagSel, setBagSel] = useState({});           // {serviceId: quantity}

  useEffect(() => {
    if (!offerId || !offerId.startsWith("off_")) {
      // Mock flight — skip ancillaries
      setLoading(false);
      setAncillaries({ available: false, mock: true });
      setSeatMaps({ data: [] });
      return;
    }
    setLoading(true);
    Promise.all([
      api.get(`/products/flights/offers/${offerId}/ancillaries`),
      api.get(`/products/flights/offers/${offerId}/seat-maps`),
    ])
      .then(([a, s]) => { setAncillaries(a.data); setSeatMaps(s.data); })
      .catch(() => { setAncillaries({ available: false }); setSeatMaps({ data: [] }); })
      .finally(() => setLoading(false));
  }, [offerId]);

  // Build duffel-pax mapping: index → duffel id (we display by index)
  const linkedPax = useMemo(() => {
    const duffelPax = ancillaries?.passengers || [];
    return duffelPax.map((dp, i) => ({
      id: dp.id, label: paxList[i] ? `${paxList[i].first_name || `P${i+1}`} ${paxList[i].last_name || ""}`.trim() : `Passenger ${i+1}`,
    }));
  }, [ancillaries, paxList]);

  // Bubble selections + extras_total up to parent
  useEffect(() => {
    const services = [];
    const selected_seats = [];
    let extras = 0;
    const bagSvcs = ancillaries?.baggage_services || [];

    // Flatten seat selections
    Object.entries(seatSel).forEach(([segId, perPax]) => {
      Object.entries(perPax).forEach(([paxId, sel]) => {
        if (sel?.serviceId) {
          const unit = parseFloat(sel.total_amount || 0);
          extras += unit;
          const desc = `Seat ${sel.designator} · ${sel.passenger_label} · ${sel.segment_label || segId}`;
          services.push({
            id: sel.serviceId,
            quantity: 1,
            type: "seat",
            description: desc,
          });
          selected_seats.push({
            designator: sel.designator,
            passenger_id: paxId,
            passenger_label: sel.passenger_label,
            segment_id: segId,
            segment_label: sel.segment_label || "",
            unit_amount: unit,
            currency: sel.total_currency || "USD",
          });
        }
      });
    });

    // Flatten baggage selections
    Object.entries(bagSel).forEach(([sid, q]) => {
      if (q > 0) {
        const svc = bagSvcs.find((x) => x.id === sid);
        const unit = svc ? parseFloat(svc.total_amount || 0) : 0;
        extras += unit * q;
        const md = svc?.metadata || {};
        const bagType = (md.type || "checked").replace("_", " ");
        const weight = md.maximum_weight_kg ? ` · up to ${md.maximum_weight_kg} kg` : "";
        services.push({
          id: sid,
          quantity: q,
          type: "baggage",
          description: `${q} × Extra ${bagType} bag${weight}`,
        });
      }
    });

    onUpdateExtras?.({
      services,
      selected_seats,
      extras_total: extras,
    });
  }, [seatSel, bagSel, ancillaries, onUpdateExtras]);

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-72 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (ancillaries?.mock) {
    return (
      <Card className="rounded-none border-[#E0DDD6] p-6" data-testid="extras-mock">
        <p className="tcp-overline text-[#C29B62] mb-3">Seats &amp; Extras</p>
        <p className="text-sm text-[#666460] flex items-center gap-2">
          <Info size={14} /> Ancillaries are only available on live Duffel fares. This itinerary is using deterministic sample data — seat &amp; baggage selection is skipped.
        </p>
      </Card>
    );
  }

  if (!ancillaries?.available) {
    return (
      <Card className="rounded-none border-[#E0DDD6] p-6" data-testid="extras-unavailable">
        <p className="tcp-overline text-[#C29B62] mb-3">Seats &amp; Extras</p>
        <p className="text-sm text-[#666460] flex items-center gap-2">
          <Info size={14} /> This fare doesn't expose extras. You can continue to confirm — your seat will be assigned at check-in.
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-5" data-testid="seats-and-extras">
      <FareRules data={ancillaries} />
      <SeatMaps
        maps={seatMaps?.data || []}
        passengers={linkedPax}
        segments={ancillaries?.segments || []}
        selection={seatSel}
        onChange={setSeatSel}
      />
      <BaggageSelector
        services={ancillaries.baggage_services}
        passengers={linkedPax}
        segments={ancillaries.segments || []}
        baggage={bagSel}
        onChange={setBagSel}
      />
    </div>
  );
}
